import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Header from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // Iter 68 — 2FA challenge state. When the backend returns `requires_2fa`
  // we hide the email/password form and reveal a 6-digit code input bound
  // to the short-lived challenge_token. The token expires in 5 minutes;
  // if the user submits stale, /auth/2fa/verify returns 400 and the user
  // is bounced back to step 1.
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  // Iter 69 — "Trust this device for 30 days" opt-in. Persisted in
  // localStorage so a user who routinely checks it stays checked next time.
  const [rememberDevice, setRememberDevice] = useState(() => {
    try { return localStorage.getItem("rc_2fa_remember_pref") === "1"; } catch { return false; }
  });
  const { login, verify2fa } = useAuth();
  const navigate = useNavigate();

  const routeForUser = (user) => {
    const role = user.role;
    if (role === "vendor") return "/dashboard/vendor";
    if (role === "admin") return "/dashboard/admin";
    if (role === "agent") return "/dashboard/agent";
    if (role === "partner") return "/dashboard/partner";
    if (role === "vendor_staff" && user.staff_role === "external_agent") return "/dashboard/partner";
    if (role === "vendor_staff") return "/dashboard/vendor";
    return "/dashboard/customer";
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) { toast.error(res.error); return; }
    if (res.requires2fa) {
      setChallenge({ token: res.challengeToken, expiresIn: res.expiresIn });
      setCode("");
      toast("Two-factor required — enter the 6-digit code from your authenticator app.");
      return;
    }
    toast.success("Welcome back");
    navigate(routeForUser(res.user));
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) { toast.error("Enter the 6-digit code from your authenticator app."); return; }
    setLoading(true);
    const res = await verify2fa(challenge.token, code, rememberDevice);
    setLoading(false);
    if (!res.ok) { toast.error(res.error); return; }
    // Iter 69 — remember the user's preference so the checkbox is
    // pre-checked next time they need to verify.
    try { localStorage.setItem("rc_2fa_remember_pref", rememberDevice ? "1" : "0"); } catch { /* ignore */ }
    toast.success("Welcome back");
    navigate(routeForUser(res.user));
  };

  return (
    <div>
      <Header />
      <div className="max-w-md mx-auto px-4 py-16">
        {!challenge ? (
          <>
            <h1 className="font-display text-3xl tracking-tight font-medium mb-2">Welcome back</h1>
            <p className="text-neutral-600 mb-8 text-sm">Log in to your RentalCar.al account.</p>

            <GoogleSignInButton />

            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs uppercase tracking-widest text-neutral-500">or</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 rounded-xl" data-testid="login-email" />
              </div>
              <div>
                <Label>Password</Label>
                <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 rounded-xl" data-testid="login-password" />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-blue-600 hover:bg-blue-700 h-11" data-testid="login-submit">
                {loading ? "Signing in…" : "Log in"}
              </Button>
            </form>
            <p className="text-sm text-center mt-4">
              <Link to="/forgot-password" className="text-neutral-600 hover:text-blue-600" data-testid="login-forgot-link">Forgot your password?</Link>
            </p>
            <p className="text-sm text-center text-neutral-500 mt-2">
              New to RentalCar.al? <Link to="/register" className="text-blue-600 font-medium" data-testid="login-to-register">Create an account</Link>
            </p>
          </>
        ) : (
          <>
            <h1 className="font-display text-3xl tracking-tight font-medium mb-2">Two-factor code</h1>
            <p className="text-neutral-600 mb-8 text-sm">
              Open your authenticator app and enter the current 6-digit code.
              This step keeps your account safe even if your password leaks.
            </p>
            <form onSubmit={submit2fa} className="space-y-4" data-testid="login-2fa-form">
              <div>
                <Label>6-digit code</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="mt-1 rounded-xl text-lg tracking-widest font-mono text-center"
                  placeholder="000000"
                  data-testid="login-2fa-code"
                  autoFocus
                />
              </div>
              {/* Iter 69 — "Trust this device for 30 days" so vendors who log
                  in daily from the same laptop don't have to type a TOTP
                  code every time. Cookie is bound to user_id, sha256-hashed
                  server-side, auto-expires after 30 days, and is wiped by
                  Vendor Settings → 2FA → Revoke trusted devices. */}
              <label className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer select-none" data-testid="login-2fa-remember-label">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="h-4 w-4 rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                  data-testid="login-2fa-remember"
                />
                <span>
                  Trust this device for 30 days
                  <span className="block text-[11px] text-neutral-500 mt-0.5">Skip the code next time on this browser.</span>
                </span>
              </label>
              <Button type="submit" disabled={loading || code.length !== 6} className="w-full rounded-full bg-blue-600 hover:bg-blue-700 h-11" data-testid="login-2fa-submit">
                {loading ? "Verifying…" : "Verify and sign in"}
              </Button>
              <button
                type="button"
                onClick={() => { setChallenge(null); setCode(""); }}
                className="block mx-auto text-sm text-neutral-500 hover:text-neutral-700"
                data-testid="login-2fa-cancel"
              >
                Use a different account
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
